// Native-browser syllabus printing.
//
// We deliberately do NOT use html2pdf/html2canvas here.
// Canvas-based PDFs are raster screenshots: text is not selectable,
// pagination can cut through paragraphs, and complex rich HTML can
// overlap. Browser print keeps the document as real text and lets the
// browser's print-layout engine paginate it.

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function flattenAccordions(root) {
  root
    .querySelectorAll('details')
    .forEach(details => {
      const replacement =
        document.createElement('div');

      replacement.className =
        'print-static-accordion';

      const summary =
        details.querySelector(
          ':scope > summary'
        );

      if (summary) {
        const heading =
          document.createElement('div');

        heading.className =
          'print-static-accordion-heading';

        // Use text only for the heading so interactive affordances,
        // disclosure triangles, "+" controls, etc. cannot leak into
        // the PDF.
        heading.textContent =
          String(
            summary.textContent || ''
          )
            .replace(/\s*\+\s*$/, '')
            .trim();

        replacement.appendChild(
          heading
        );
      }

      const body =
        document.createElement('div');

      body.className =
        'print-static-accordion-body';

      Array.from(
        details.children
      )
        .filter(
          child =>
            child.tagName !==
            'SUMMARY'
        )
        .forEach(child => {
          body.appendChild(
            child.cloneNode(true)
          );
        });

      replacement.appendChild(body);

      details.replaceWith(
        replacement
      );
    });
}

function removeInteractiveChrome(root) {
  root
    .querySelectorAll(
      'button, input, select, textarea'
    )
    .forEach(element => {
      element.remove();
    });

  root
    .querySelectorAll(
      '[contenteditable]'
    )
    .forEach(element => {
      element.removeAttribute(
        'contenteditable'
      );
    });
}

function normaliseRichText(root) {
  root
    .querySelectorAll(
      '.print-rich-text *'
    )
    .forEach(element => {
      // Pasted HTML can carry layout styles from Word, Google Docs,
      // email, etc. Those are useful on screen sometimes but are a
      // common cause of overlapping print text.
      element.style.removeProperty(
        'position'
      );
      element.style.removeProperty(
        'top'
      );
      element.style.removeProperty(
        'right'
      );
      element.style.removeProperty(
        'bottom'
      );
      element.style.removeProperty(
        'left'
      );
      element.style.removeProperty(
        'transform'
      );
      element.style.removeProperty(
        'height'
      );
      element.style.removeProperty(
        'max-height'
      );
      element.style.removeProperty(
        'overflow'
      );
      element.style.removeProperty(
        'float'
      );

      if (
        element.tagName !== 'PRE' &&
        element.tagName !== 'CODE'
      ) {
        element.style.removeProperty(
          'white-space'
        );
      }
    });
}

function waitForImages(doc) {
  const images =
    Array.from(
      doc.images || []
    );

  return Promise.all(
    images.map(image => {
      if (image.complete) {
        return Promise.resolve();
      }

      return new Promise(resolve => {
        const finish = () =>
          resolve();

        image.addEventListener(
          'load',
          finish,
          { once: true }
        );

        image.addEventListener(
          'error',
          finish,
          { once: true }
        );
      });
    })
  );
}

export async function downloadSyllabusPdfRoot(
  root,
  fileName
) {
  if (!root) {
    throw new Error(
      'No syllabus content was available to print.'
    );
  }

  const printable =
    root.cloneNode(true);

  flattenAccordions(printable);
  removeInteractiveChrome(printable);
  normaliseRichText(printable);

  const frame =
    document.createElement(
      'iframe'
    );

  frame.setAttribute(
    'aria-hidden',
    'true'
  );

  Object.assign(
    frame.style,
    {
      position: 'fixed',
      width: '210mm',
      height: '297mm',
      border: '0',
      left: '-10000px',
      top: '0',
      pointerEvents: 'none'
    }
  );

  document.body.appendChild(
    frame
  );

  const printDocument =
    frame.contentDocument;

  const printWindow =
    frame.contentWindow;

  if (
    !printDocument ||
    !printWindow
  ) {
    frame.remove();

    throw new Error(
      'The browser could not create the print document.'
    );
  }

  const title =
    String(
      fileName ||
      'Syllabus'
    )
      .replace(/\.pdf$/i, '');

  printDocument.open();

  printDocument.write(`
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>

  <style>
    @page {
      size: A4 portrait;
      margin: 13mm 14mm 15mm;
    }

    html,
    body {
      margin: 0;
      padding: 0;
      background: #ffffff;
      color: #243b53;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 10.5pt;
      line-height: 1.48;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    [data-syllabus-print-root] {
      display: block !important;
      width: auto !important;
      max-width: none !important;
      margin: 0 !important;
      padding: 0 !important;
      background: #ffffff !important;
      color: #243b53 !important;
      font-family: Arial, Helvetica, sans-serif !important;
      font-size: 10.5pt !important;
      line-height: 1.48 !important;
      box-sizing: border-box !important;
    }

    [data-syllabus-print-root],
    [data-syllabus-print-root] * {
      box-sizing: border-box;
    }

    [data-syllabus-print-root] h1 {
      margin: 0 0 8pt;
      font-size: 20pt;
      line-height: 1.2;
      break-after: avoid-page;
      page-break-after: avoid;
    }

    [data-syllabus-print-root] h2 {
      margin: 18pt 0 8pt;
      padding-bottom: 4pt;
      border-bottom: 1px solid #d9e2ec;
      font-size: 15pt;
      line-height: 1.25;
      break-after: avoid-page;
      page-break-after: avoid;
    }

    [data-syllabus-print-root] h3,
    [data-syllabus-print-root] h4 {
      break-after: avoid-page;
      page-break-after: avoid;
    }

    [data-syllabus-print-root]
      .print-subtitle {
      color: #627d98;
      margin-bottom: 18pt;
      font-size: 10pt;
    }

    [data-syllabus-print-root]
      .print-section {
      display: block !important;
      margin: 0 0 18pt;
      break-inside: auto;
      page-break-inside: auto;
    }

    [data-syllabus-print-root]
      .print-rich-text {
      display: block !important;
      position: static !important;
      overflow: visible !important;
      height: auto !important;
      max-height: none !important;
      white-space: normal;
      overflow-wrap: break-word;
      word-break: normal;
    }

    [data-syllabus-print-root]
      .print-rich-text * {
      position: static !important;
      transform: none !important;
      float: none !important;
      height: auto !important;
      min-height: 0 !important;
      max-height: none !important;
      overflow: visible !important;
    }

    [data-syllabus-print-root]
      .print-rich-text p {
      display: block !important;
      margin: 0 0 7pt !important;
      line-height: 1.48 !important;
      widows: 3;
      orphans: 3;
    }

    [data-syllabus-print-root]
      .print-rich-text li {
      line-height: 1.48 !important;
      widows: 3;
      orphans: 3;
    }

    [data-syllabus-print-root]
      .print-rich-text ul,
    [data-syllabus-print-root]
      .print-rich-text ol {
      margin: 5pt 0 8pt;
      padding-left: 22pt;
    }

    [data-syllabus-print-root]
      .print-rich-text blockquote {
      margin: 8pt 0 8pt 18pt;
    }

    [data-syllabus-print-root]
      .print-static-accordion {
      display: block !important;
      margin: 7pt 0 10pt;
      padding: 0;
      border: 1px solid #d9e2ec;
      border-radius: 4pt;
      break-inside: auto;
      page-break-inside: auto;
      overflow: visible !important;
    }

    [data-syllabus-print-root]
      .print-static-accordion-heading {
      display: block !important;
      padding: 7pt 9pt;
      background: #f3f6f8;
      color: #243b53;
      font-weight: 700;
      break-after: avoid-page;
      page-break-after: avoid;
    }

    [data-syllabus-print-root]
      .print-static-accordion-body {
      display: block !important;
      padding: 8pt 9pt;
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
    }

    [data-syllabus-print-root]
      .print-static-accordion-body
      > :first-child {
      margin-top: 0 !important;
    }

    [data-syllabus-print-root]
      .print-static-accordion-body
      > :last-child {
      margin-bottom: 0 !important;
    }

    [data-syllabus-print-root]
      .print-schedule-row {
      display: grid !important;
      grid-template-columns:
        88pt 1fr !important;
      gap: 10pt;
      padding: 7pt 0;
      border-bottom:
        1px solid #e5eaee;
      break-inside: avoid-page;
      page-break-inside: avoid;
    }

    [data-syllabus-print-root]
      .print-schedule-week {
      color: #627d98;
      font-size: 9pt;
    }

    [data-syllabus-print-root]
      .print-ai-permission {
      break-inside: avoid-page;
      page-break-inside: avoid;
      margin-bottom: 8pt;
    }

    [data-syllabus-print-root] table {
      width: 100% !important;
      border-collapse: collapse !important;
      table-layout: auto !important;
      margin: 7pt 0 10pt;
    }

    [data-syllabus-print-root] thead {
      display: table-header-group;
    }

    [data-syllabus-print-root] tr {
      break-inside: avoid-page;
      page-break-inside: avoid;
    }

    [data-syllabus-print-root] th,
    [data-syllabus-print-root] td {
      border: 1px solid #d9e2ec;
      padding: 4pt 5pt;
      vertical-align: top;
      height: auto !important;
      min-height: 0 !important;
      overflow-wrap: break-word;
    }

    [data-syllabus-print-root] img {
      display: block;
      position: static !important;
      max-width: 100% !important;
      height: auto !important;
      margin: 7pt auto;
      break-inside: avoid-page;
      page-break-inside: avoid;
    }

    [data-syllabus-print-root] a {
      color: inherit;
      text-decoration: underline;
    }

    [data-syllabus-print-root] pre,
    [data-syllabus-print-root] code {
      white-space: pre-wrap !important;
      overflow-wrap: anywhere;
    }

    @media print {
      html,
      body {
        width: auto !important;
        height: auto !important;
        overflow: visible !important;
      }
    }
  </style>
</head>

<body>
  ${printable.outerHTML}
</body>
</html>
  `);

  printDocument.close();

  await waitForImages(
    printDocument
  );

  // Give the print frame one layout cycle after all images have settled.
  await new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(
        resolve
      );
    });
  });

  let cleaned = false;

  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;

    window.setTimeout(
      () => {
        frame.remove();
      },
      100
    );
  };

  printWindow.addEventListener(
    'afterprint',
    cleanup,
    { once: true }
  );

  const previousDocumentTitle =
    document.title;

  document.title =
    title;

  printWindow.focus();

  try {
    printWindow.print();
  } finally {
    /*
     * Keep the content-specific title long enough
     * for the browser print/save dialog to capture it,
     * then restore the application title.
     */
    window.setTimeout(
      () => {
        document.title =
          previousDocumentTitle;
      },
      1000
    );
  }

  // Some browsers do not reliably fire afterprint for an iframe.
  window.setTimeout(
    cleanup,
    60000
  );

  return null;
}
